import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, input, signal, Type } from '@angular/core';
import { ServiceStateEnum } from '@mwe/constants';
import { BackendProcessStore, StaticContentService, StepInterface, StepStateInterface } from '@mwe/services';
import { BasicCardComponent } from '../../cards/basic-card/basic-card.component';
import { TranslateDirective } from '@ngx-translate/core';
import { AlertComponent } from '../../alert/alert.component';
import { NgClass, NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import { NextStepsComponent } from '../../next-steps/next-steps.component';
import { AddStaticImagePathPipe } from '@mwe/ui/pipes';

type StepRepresentation = { step: StepInterface; state?: StepStateInterface };
export type InfoStep = Pick<StepInterface, 'title' | 'label'> & Partial<Pick<StepInterface, 'translationParams'>>;

@Component({
  selector: 'mwe-backend-process-presentation-component',
  templateUrl: './backend-process-presentation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BasicCardComponent,
    TranslateDirective,
    AlertComponent,
    NgTemplateOutlet,
    NextStepsComponent,
    NgComponentOutlet,
    NgClass,
    AddStaticImagePathPipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class BackendProcessPresentationComponent {
  processTitle = input<string>('');
  infoSteps = input<InfoStep[]>([]);
  infoTitleKey = input<string>('summary.whatNext');
  errorTitleKey = input<string>('process.default.error.title');
  errorMsgKey = input<string>('process.default.error.message');
  errorMsgRetryKey = input<string>('process.default.error.message_retry');
  workInProgressMsgKey = input<string>('process.default.loading.title');
  workInProgressSubtextKey = input<string>('process.default.loading.message');
  successMsgKey = input<string>('process.default.success.title');
  successSubtextKey = input<string>('process.default.success.message');
  minLoadingSpinnerDisplayTime = input<number>(2000);
  alertPlaceholders = signal<{ [key: string]: string }>(null);

  /**
   * Optional component to render the steps
   * Will get step: StepInterface and state: StepStateInterface as inputs
   */
  stepRenderer = signal<Type<unknown>>(null);

  minLoadingSpinnerDisplayTimePassed = signal(false);
  minLoadingSpinnerDisplayTimeout: unknown;

  process = inject(BackendProcessStore);
  private staticContentService = inject(StaticContentService);

  constructor() {
    effect(() => {
      const state = this.process.processState();
      const isFinished = state === ServiceStateEnum.SUCCESS || state === ServiceStateEnum.FAILED || state === ServiceStateEnum.ERROR;
      const isLoading = state === ServiceStateEnum.LOADING;
      if ((isFinished || isLoading) && !this.minLoadingSpinnerDisplayTimeout) {
        // Show loading spinner for at least 2 seconds
        this.minLoadingSpinnerDisplayTimeout = setTimeout(() => {
          this.minLoadingSpinnerDisplayTimePassed.set(true);
        }, this.minLoadingSpinnerDisplayTime());
      }
    });
  }

  steps = computed<StepRepresentation[]>(() => {
    const steps = Array.from(this.process.steps().entries());
    return steps.map(([step, state]) => ({ step, state }));
  });

  isFirstStepError = computed(() => {
    const steps = this.steps();
    if (steps.length === 0) {
      return false;
    }
    const firstStepState = steps[0].state;
    if (!firstStepState) {
      return false;
    }
    return firstStepState.state === ServiceStateEnum.ERROR || firstStepState.state === ServiceStateEnum.FAILED;
  });

  showLoadingState = computed(() => {
    const processState = this.process.processState();
    const processInALoadingState = processState === ServiceStateEnum.INIT || processState === ServiceStateEnum.LOADING;
    return processInALoadingState || !this.minLoadingSpinnerDisplayTimePassed();
  });

  showErrorState = computed(
    () =>
      !this.showLoadingState() &&
      (this.process.processState() === ServiceStateEnum.ERROR || this.process.processState() === ServiceStateEnum.FAILED),
  );

  showSuccessState = computed(() => !this.showLoadingState() && this.process.processState() === ServiceStateEnum.SUCCESS);

  retryAllowed = computed(() => this.process.retryAllowed());

  getIconClassForStep(item: StepRepresentation): string | undefined {
    const { step, state } = item;
    const icon = step.iconMap ? step.iconMap.get(state.state) : undefined;
    const cls = step.classMap ? step.classMap.get(state.state) : undefined;
    return icon ? `fas ${icon} ${cls ?? ''}` : undefined;
  }

  getTitleForStep(item: StepRepresentation): string {
    const { step, state } = item;
    const defaultTitle = step.title;
    const stateTitle = step.textMap.get(state.state)?.title;
    return stateTitle ?? defaultTitle;
  }

  getLabelsForStep(item: StepRepresentation): string[] {
    const { step, state } = item;
    const defaultLabel = step?.label ? (Array.isArray(step.label) ? step.label : [step.label]) : undefined;
    let stateLabel = step.textMap.get(state.state)?.label;
    stateLabel = stateLabel ? (Array.isArray(stateLabel) ? stateLabel : [stateLabel]) : undefined;
    return stateLabel ?? defaultLabel;
  }

  getImagePath() {
    return this.staticContentService.getImagePath();
  }
}
